// PROA_TRIAL_30D_UNIFIED_V1
// src/pages/SubscriptionPage.tsx

import React, { useState } from 'react';
import { 
  Container, Typography, Box, Grid, Paper, Button, Chip, 
  Stack, Switch, Divider, CircularProgress, Alert, alpha 
} from '@mui/material';
import { motion } from "framer-motion";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';


import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axiosInstance';
import MySubscriptionPanel from '../modules/pricingJourney/components/MySubscriptionPanel';
import PageHeader from '../modules/pricingJourney/components/PageHeader';

// --- ANIMATION WRAPPER (Idéntico a LandingPage) ---
const RevealSection = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, delay, ease: "easeOut" }}
        style={{ height: '100%' }}
    >
        {children}
    </motion.div>
);

// --- CONFIGURACIÓN DE NEGOCIO ---
const TIER_LEVELS: Record<string, number> = {
  'discovery': 0,
  'growth': 1,
  'autonomy': 2
};

const PRICE_KEYS_MONTHLY: Record<string, string> = {
  'growth': 'growth_monthly',
  'autonomy': 'autonomy_monthly'
};
const PRICE_KEYS_YEARLY: Record<string, string> = {
  'growth': 'growth_yearly',
  'autonomy': 'autonomy_yearly'
};

const SubscriptionPage: React.FC = () => {
  const { user, pricingAccess } = useAuth();

  // --- ESTADO ---
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // El backend devuelve 409 si ya hay una suscripción abierta en Stripe.
  const [subscriptionExistsMsg, setSubscriptionExistsMsg] = useState<string | null>(null);

  // --- DETERMINACIÓN DEL TIER ACTUAL ---
  // Si user.tier_id no viene, asumimos 'discovery' (Fail-safe)
  const currentTierId = user?.tier_id || 'discovery';
  const currentLevel = TIER_LEVELS[currentTierId] ?? 0;
  // PRC-021: vuelta de Stripe tras cancelar el pago.
  const [searchParams] = useSearchParams();
  const paymentCancelled = searchParams.get('pago') === 'cancelado';

  // PRC-023: un cliente con suscripción vigente ve «Mi suscripción» (E1/E3).
  if (pricingAccess?.state === 'paid') {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1180 }}>
        <MySubscriptionPanel />
      </Box>
    );
  }

  // --- LÓGICA CENTRAL DE ACCIONES (LA MATRIZ DE DECISIÓN) ---
  const executePlanAction = async (targetTierId: string, actionType: 'CHECKOUT' | 'PORTAL') => {
    setLoadingAction(targetTierId);
    setErrorMsg(null);
    setSubscriptionExistsMsg(null);

    try {
      if (actionType === 'PORTAL') {
        // Redirigir al Portal de Cliente de Stripe
        const response = await axiosInstance.post('/subscriptions/create-portal-session');
        window.location.href = response.data.url;
      } else {
        // Redirigir a Checkout (Nueva Compra)
        const priceMap = billingCycle === 'monthly' ? PRICE_KEYS_MONTHLY : PRICE_KEYS_YEARLY;
        const priceKey = priceMap[targetTierId];
        
        const response = await axiosInstance.post('/subscriptions/create-checkout-session', { 
          price_key: priceKey 
        });
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error("Error en transacción:", error);
      if (actionType === 'CHECKOUT' && error?.response?.status === 409) {
        setSubscriptionExistsMsg(
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : 'Ya tienes una suscripción: gestiónala desde el portal de facturación.',
        );
      } else {
        setErrorMsg("No se pudo conectar con la pasarela de pagos. Por favor, inténtalo de nuevo.");
      }
      setLoadingAction(null);
    }
  };

  // --- DATA DEFINITION ---
  const PLANS = [
    {
      id: 'discovery',
      title: 'DISCOVERY',
      price: 0,
      yearlyPrice: 0,
      description: 'Auditoría inicial forense para detectar fugas de margen evidentes.',
      color: '#94a3b8', // Slate 400
      features: [
        'Carga de Datos Manual (CSV/Excel)', 
        'Auditoría Profit Leakage', 
        'Acceso Business Intelligence',
        'Soporte Básico'
      ]
    },
    {
      id: 'growth',
      title: 'GROWTH',
      price: 990,
      yearlyPrice: 9900, // 825/mes
      description: 'Gestión dinámica y operativa masiva para equipos de alto rendimiento.',
      color: '#38bdf8', // Cyan 400
      features: [
        'Carga de Datos Automatizada', 
        'Optimizaciones Ilimitadas', 
        'Optimización por Lotes',
        'Entrenamiento Machine Learning Mensual', 
        'Predicción de Demanda (3 Meses)'
      ]
    },
    {
      id: 'autonomy',
      title: 'AUTONOMY',
      price: 1490,
      yearlyPrice: 14900, // 1241/mes
      description: 'Integración total con ERP y consultoría estratégica dedicada.',
      color: '#fcd34d', // Amber 300
      features: [
        'Conexión ERP',
        'Entrenamiento Machine Learning Semanal',
        'Predicción Estratégica (12 meses)', 
        'Consultor Asignado'
      ]
    }
  ].filter((plan) => plan.id !== 'discovery'); // Sin nivel gratuito permanente.

  return (
    // CONTENEDOR PRINCIPAL: COLOR SÓLIDO SIN GRADIENTES NI IMÁGENES DE FONDO
    <Box sx={{ 
        bgcolor: '#0f172a', 
        minHeight: '100vh', 
        py: 3, 
        position: 'relative', 
        overflowX: 'hidden' 
    }}>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            
            {/* CABECERA */}
            <RevealSection>
                <Box sx={{ mb: 6 }}>
                    {/* PRC-032: cabecera común. */}
                    <PageHeader
                      eyebrow="Mi cuenta"
                      title="Mi suscripción"
                      description="Tu plan, la facturación y el acceso de tu cuenta."
                      back="origin"
                    />

                    {pricingAccess?.state === 'trial' && (
                      <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                        Tienes 30 días de prueba sin tarjeta ni cobro automático.
                        Puedes contratar Growth cuando quieras. Tus datos se conservarán.
                      </Alert>
                    )}
                    {pricingAccess && !pricingAccess.has_access && (
                      <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                        Tu acceso operativo a PROAPricing está pausado.
                        Contrata Growth para recuperar tus datos y continuar trabajando.
                      </Alert>
                    )}
                    {paymentCancelled && <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>No se ha realizado ningún cargo.</Alert>}
                    {errorMsg && <Alert severity="error" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>{errorMsg}</Alert>}
                    {subscriptionExistsMsg && (
                      <Alert
                        severity="info"
                        sx={{ mb: 4, maxWidth: 600, mx: 'auto', textAlign: 'left' }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            disabled={!!loadingAction}
                            onClick={() => executePlanAction('portal', 'PORTAL')}
                          >
                            Ir al portal
                          </Button>
                        }
                      >
                        {subscriptionExistsMsg}
                      </Alert>
                    )}

                    {/* TOGGLE SWITCH */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.03)', p: 1, borderRadius: 10, border: '1px solid #334155' }}>
                            <Typography variant="body2" sx={{ color: billingCycle === 'monthly' ? '#f8fafc' : '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={() => setBillingCycle('monthly')}>
                                Mensual
                            </Typography>
                            
                            <Switch 
                                checked={billingCycle === 'yearly'}
                                onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#38bdf8' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#38bdf8' },
                                }}
                            />
                            
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ color: billingCycle === 'yearly' ? '#f8fafc' : '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={() => setBillingCycle('yearly')}>
                                    Anual
                                </Typography>
                                <Chip label="2 meses gratis" size="small" sx={{ bgcolor: '#fcd34d', color: '#0f172a', fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </RevealSection>

            {/* GRID DE CARDS */}
            <Grid container spacing={4} alignItems="flex-start">
                
                {PLANS.map((plan, index) => {
                    const isCurrent = currentTierId === plan.id;
                    const planLevel = TIER_LEVELS[plan.id];
                    
                    // Cálculo de precios
                    const monthlyPrice = billingCycle === 'monthly' ? plan.price : Math.round(plan.yearlyPrice / 12);
                    
                    // --- DETERMINACIÓN DE ESTADO DEL BOTÓN ---
                    let btnLabel = '';
                    let btnAction: 'CHECKOUT' | 'PORTAL' | 'DISABLED' = 'DISABLED';
                    let btnVariant: 'contained' | 'outlined' = 'outlined';

                    if (isCurrent) {
                        // CASO: ES EL PLAN ACTUAL
                        if (currentLevel === 0) {
                             // Discovery Actual -> Solo informativo
                             btnLabel = "TU PLAN ACTUAL";
                             btnAction = 'DISABLED';
                        } else {
                             // Pagado Actual -> Gestionar en Portal
                             btnLabel = "GESTIONAR SUSCRIPCIÓN";
                             btnAction = 'PORTAL';
                             btnVariant = 'outlined';
                        }
                    } else {
                        // CASO: NO ES EL PLAN ACTUAL
                        if (currentLevel === 0) {
                            // Usuario es Discovery -> UPGRADE -> Checkout
                            btnLabel = "MEJORAR PLAN";
                            btnAction = 'CHECKOUT';
                            btnVariant = 'contained';
                        } else {
                            // Usuario ya paga (Growth/Autonomy) -> Cualquier cambio via Portal (Stripe maneja prorrateo)
                            btnAction = 'PORTAL';
                            if (planLevel > currentLevel) {
                                btnLabel = "MEJORAR PLAN"; // Upgrade via Portal
                                btnVariant = 'contained';
                            } else {
                                btnLabel = planLevel === 0 ? "CANCELAR SUSCRIPCIÓN" : "DEGRADAR PLAN"; // Downgrade via Portal
                                btnVariant = 'outlined';
                            }
                        }
                    }

                    return (
                        <Grid item xs={12} md={4} key={plan.id}>
                            <RevealSection delay={index * 0.1}>
                                <Paper 
                                    elevation={isCurrent ? 24 : 0}
                                    sx={{ 
                                        p: 4, 
                                        height: '100%',
                                        position: 'relative',
                                        bgcolor: alpha('#1e293b', 0.4), // Slate 800 con transparencia
                                        backdropFilter: 'blur(12px)',
                                        borderRadius: 4,
                                        // Highlight lógica: Si es el actual, borde brillante y escala
                                        border: isCurrent 
                                            ? `2px solid ${plan.color}` 
                                            : `1px solid ${alpha('#334155', 0.5)}`,
                                        transform: isCurrent ? { xs: 'none', md: 'scale(1.05)' } : 'none',
                                        zIndex: isCurrent ? 2 : 1,
                                        transition: 'all 0.3s ease',
                                        boxShadow: isCurrent ? `0 0 40px ${alpha(plan.color, 0.2)}` : 'none',
                                        '&:hover': {
                                            borderColor: plan.color,
                                            transform: isCurrent ? { xs: 'none', md: 'scale(1.05)' } : 'translateY(-5px)'
                                        }
                                    }}
                                >
                                    {/* BADGE "PLAN ACTUAL" */}
                                    {isCurrent && (
                                       <Chip 
                                        label="TU PLAN ACTUAL" 
                                        size="small" 
                                        sx={{ 
                                            position: 'absolute', top: -12, right: '50%', transform: 'translateX(50%)', 
                                            bgcolor: plan.color, color: '#0f172a', fontWeight: 'bold', letterSpacing: 1,
                                            boxShadow: `0 0 15px ${alpha(plan.color, 0.6)}`
                                        }} 
                                       />
                                    )}

                                    {/* HEADER CARD CON PRECIO DE ANCLAJE */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ color: plan.color, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
                                            {plan.title}
                                        </Typography>
                                        
                                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'baseline' }}>
                                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                                                {plan.price === 0 ? '0€' : `${monthlyPrice}€`}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#64748b', ml: 1 }}>/mes</Typography>

                                            {/* LÓGICA DE PRECIO TACHADO (NUEVO) */}
                                            {billingCycle === 'yearly' && plan.price > 0 && (
                                                <Typography variant="body1" sx={{ ml: 2, color: '#64748b', textDecoration: 'line-through', fontWeight: 500 }}>
                                                    {plan.price}€
                                                </Typography>
                                            )}
                                        </Box>
                                        
                                        {/* Subtexto Anual */}
                                        <Typography variant="caption" sx={{ 
                                            color: billingCycle === 'yearly' && plan.yearlyPrice > 0 ? plan.color : 'transparent', 
                                            display: 'block', mt: 0.5, fontWeight: 600, minHeight: '20px' 
                                        }}>
                                            {billingCycle === 'yearly' && plan.yearlyPrice > 0 ? `Facturado ${plan.yearlyPrice.toLocaleString()}€ / año` : ''}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, minHeight: '48px', lineHeight: 1.6 }}>
                                        {plan.description}
                                    </Typography>

                                    {/* ACTION BUTTON */}
                                    <Button 
                                        fullWidth 
                                        size="large" 
                                        variant={btnVariant}
                                        disabled={btnAction === 'DISABLED' || !!loadingAction}
                                        onClick={() => btnAction !== 'DISABLED' && executePlanAction(plan.id, btnAction)}
                                        sx={{ 
                                            mb: 4, py: 1.5, fontWeight: 800,
                                            ...(btnVariant === 'contained' ? {
                                                bgcolor: plan.color, color: '#0f172a',
                                                '&:hover': { bgcolor: plan.color, filter: 'brightness(1.1)', boxShadow: `0 0 20px ${alpha(plan.color, 0.4)}` }
                                            } : {
                                                borderColor: isCurrent ? plan.color : alpha(plan.color, 0.5), 
                                                color: isCurrent ? plan.color : plan.color,
                                                opacity: btnAction === 'DISABLED' ? 0.6 : 1,
                                                '&:hover': { borderColor: plan.color, bgcolor: alpha(plan.color, 0.1) }
                                            })
                                        }}
                                    >
                                        {loadingAction === plan.id ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            btnLabel
                                        )}
                                    </Button>

                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 4 }} />

                                    {/* FEATURES LIST */}
                                    <Stack spacing={2}>
                                        {plan.features.map((feature, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ fontSize: 20, mr: 1.5, color: isCurrent ? plan.color : '#475569' }} />
                                                <Typography variant="body2" sx={{ color: isCurrent ? '#f1f5f9' : '#94a3b8' }}>
                                                    {feature}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>

                                </Paper>
                            </RevealSection>
                        </Grid>
                    );
                })}

            </Grid>

            {/* FOOTER - GESTIÓN EXTRA (Solo para usuarios de pago) */}
            {currentLevel > 0 && (
                <RevealSection delay={0.5}>
                    <Box sx={{ mt: 10, textAlign: 'center', opacity: 0.8, p: 4, border: '1px dashed #334155', borderRadius: 4 }}>
                        <Typography variant="h6" color="#f8fafc" gutterBottom>
                            ¿Necesitas descargar tus facturas?
                        </Typography>
                        <Typography variant="body2" color="#94a3b8" sx={{ mb: 2 }}>
                            Accede al portal seguro de Stripe para ver tu historial de pagos, actualizar tu tarjeta o editar tus datos fiscales.
                        </Typography>
                        <Button 
                            onClick={() => executePlanAction('portal', 'PORTAL')} 
                            startIcon={<AutoAwesomeIcon />}
                            sx={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 700 }}
                        >
                            Acceder al Portal de Facturación
                        </Button>
                    </Box>
                </RevealSection>
            )}

        </Container>
    </Box>
  );
};

export default SubscriptionPage;